import { Dispatch, SetStateAction } from 'react'

import { IContestsStorePayload } from '../../../../services/Contests/interfaces'

/** Props every tab of the contest form receives. The modal stays the single owner
 *  of the form state, so each tab keeps using the same `setForm({ ...form, x })`. */
export interface IContestFormTabProps {
  form: IContestsStorePayload
  setForm: Dispatch<SetStateAction<IContestsStorePayload>>
  errors: Record<string, string>
  saving: boolean
}

export const CONTEST_TAB_LABELS = ['Geral', 'Datas', 'Evento', 'Inscrição', 'Obras', 'Regulamento', 'Aparência']

export const ARTWORKS_TAB_INDEX = 4

export const APPEARANCE_TAB_INDEX = 6

/** Public contest pages that accept a custom background: the options screen (ABRA-348) and the steps (ABRA-336). */
export type TContestBackgroundStep = 'menu' | 'registration' | 'submission'

export const CONTEST_BACKGROUND_STEPS: TContestBackgroundStep[] = ['menu', 'registration', 'submission']

/** Per step: a newly picked File, the URL already saved, or null for no background. */
export type TContestBackgrounds = Record<TContestBackgroundStep, File | string | null>

export const EMPTY_CONTEST_BACKGROUNDS: TContestBackgrounds = { menu: null, registration: null, submission: null }

/** Steps whose background the user asked to remove with "Remover fundo". */
export type TRemovedBackgrounds = Record<TContestBackgroundStep, boolean>

export const NO_REMOVED_BACKGROUNDS: TRemovedBackgrounds = { menu: false, registration: false, submission: false }

/**
 * Which fields each tab renders, in tab order. With the form split across tabs a
 * validation error can land on a hidden panel, so this drives the per-tab error
 * badge and the jump to the first offending tab after a failed save.
 */
const CONTEST_TAB_FIELDS: string[][] = [
  ['name', 'link_type', 'slug', 'courses_ids', 'teachers_ids', 'whatsapp_group_link'],
  [
    'registration_deadline',
    'submission_deadline',
    'delivery_deadline',
    'opening_event_date',
    'withdraw_start_date',
    'withdraw_end_date'
  ],
  ['event_id', 'contest_participants_ticket_id', 'guest_ticket_id'],
  ['registration_fee', 'max_installments', 'max_registrations', 'registration_fee_items'],
  [
    'min_arts_per_application',
    'max_arts_per_application',
    'frame_value_mode',
    'frame_value',
    'default_starting_bid',
    'commission_type',
    'commission_value',
    'art_fields',
    'art_fields_notice'
  ],
  ['contest_rules'],
  ['menu_background', 'registration_background', 'submission_background']
]

/** Laravel returns dotted paths ("registration_fee_items.0"); only the root maps to a tab. */
const rootKey = (key: string) => key.split('.')[0]

const hasMessage = (value: unknown) => (Array.isArray(value) ? value.length > 0 : Boolean(value))

/** Number of error messages sitting in each tab, in tab order. */
export function countErrorsPerTab(errors: Record<string, unknown>): number[] {
  const counts = CONTEST_TAB_FIELDS.map(() => 0)

  Object.entries(errors).forEach(([key, value]) => {
    if (!hasMessage(value)) return
    const tabIndex = CONTEST_TAB_FIELDS.findIndex((fields) => fields.includes(rootKey(key)))
    if (tabIndex >= 0) counts[tabIndex] += 1
  })

  return counts
}

/** Index of the first tab holding an error, or -1 when there is none. */
export function firstTabWithErrors(errors: Record<string, unknown>): number {
  return countErrorsPerTab(errors).findIndex((count) => count > 0)
}
